import { type FormEvent, useState } from "react";
import { AppIcon, Button, FormField, Input, SpectrumBar, Wordmark } from "@praesto/ui-kit";
import { AppCheckRefusedError } from "../lib/app-check";
import { legalUrl } from "../lib/website-url";
import itLocale from "../locales/it.json";
import { RecaptchaNotice } from "./RecaptchaNotice";

const copy = itLocale as Readonly<Record<string, string>>;

/**
 * Access screen, C3F v18 (reference: design/system/ui_kits/app/AuthScreens.jsx).
 * Split layout: the form column on Paper (wordmark + editorial title), and an
 * always-Night claim panel on the right — hidden below lg, phones get the
 * form alone. The old "PRAESTO · slug" mono header was the pre-v18 identity:
 * the brand arrives as files, and the structure gets named after sign-in.
 */
export function LoginView({
  onSignIn,
  onReset,
}: {
  tenantSlug: string;
  onSignIn: (email: string, password: string) => Promise<void>;
  /** Send a password-reset email. Optional so hosts that don't wire it hide the link. */
  onReset?: (email: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<"login" | "forgot" | "sent">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSignIn(email, password);
    } catch {
      setError("Credenziali non valide o accesso non riuscito.");
      setBusy(false);
    }
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onReset?.(email);
    } catch (e) {
      // An App Check refusal (C-16) happens before any lookup, so saying it
      // reveals nothing about the address — and "check your email" would be
      // false: nothing was sent.
      if (e instanceof AppCheckRefusedError) {
        setError(copy["login.reset.appCheckFailed"] ?? null);
        setBusy(false);
        return;
      }
      // Intentionally swallow auth/user-not-found (and any other error): we must
      // not reveal whether the address exists. Always show the same outcome.
    }
    setBusy(false);
    setMode("sent");
  }

  return (
    <div className="flex min-h-screen bg-canvas text-fg-default">
      {/* Form column */}
      <div className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="grid w-full max-w-sm gap-6">
          <Wordmark height={34} />

          {mode === "sent" ? (
            <div>
              <h1 className="text-h3 font-semibold text-fg-strong">Controlla l&apos;email</h1>
              <p className="mt-4 text-sm leading-relaxed text-fg-muted">
                Se <span className="text-fg-default">{email}</span> è registrata, ti abbiamo inviato
                un link per reimpostare la password. Controlla anche lo spam.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                fullWidth
                className="mt-8"
                onClick={() => {
                  setMode("login");
                  setError(null);
                }}
              >
                Torna al login
              </Button>
            </div>
          ) : mode === "forgot" ? (
            <form onSubmit={submitReset}>
              <h1 className="text-h3 font-semibold text-fg-strong">Reimposta la password</h1>
              <p className="mt-3 text-sm leading-relaxed text-fg-muted">
                Inserisci la tua email: ti invieremo un link per scegliere una nuova password.
              </p>
              <div className="mt-6">
                <FormField label="Email">
                  <Input
                    id="reset-email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormField>
              </div>
              {error && (
                <p className="mt-4 text-sm text-danger" role="alert">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={busy}
                variant="primary"
                size="lg"
                fullWidth
                className="mt-8"
              >
                {busy ? "Invio…" : "Invia link di reset"}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setMode("login");
                  setError(null);
                }}
                className="tap-target mt-4 w-full text-sm text-fg-muted hover:text-fg-default"
              >
                ← Torna al login
              </button>
              {/* The reset-link request is the step that carries an App Check
                  token (`sendPasswordReset`), not the page the email opens. */}
              <RecaptchaNotice
                text={copy["login.reset.recaptcha.text"] ?? ""}
                linkText={copy["login.reset.recaptcha.link_text"] ?? ""}
                href={legalUrl("it", "/privacy#recaptcha")}
              />
            </form>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-h3 font-semibold text-fg-strong">Entra e vedi il tuo turno.</h1>
              <p className="mt-2 text-sm text-fg-muted">Non serve cercare nulla: è già pronto.</p>

              <div className="mt-6">
                <FormField label="Email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormField>
              </div>

              <div className="mt-4">
                <FormField label="Password">
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </FormField>
              </div>

              {error && (
                <p className="mt-4 text-sm text-danger" role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={busy}
                variant="primary"
                size="lg"
                fullWidth
                className="mt-8"
              >
                {busy ? "Accesso…" : "Entra in Praesto"}
              </Button>

              {onReset && (
                <button
                  type="button"
                  onClick={() => {
                    setMode("forgot");
                    setError(null);
                  }}
                  className="tap-target mt-4 w-full text-sm text-fg-muted hover:text-fg-default"
                >
                  Password dimenticata?
                </button>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Claim panel — always Night in both themes, so it carries its own
          token family and desaturated ink (surface-night). */}
      <div className="surface-night hidden flex-1 place-items-center bg-praesto-night p-12 text-praesto-paper lg:grid">
        <div className="grid max-w-[420px] gap-5">
          <AppIcon size={64} />
          <p className="text-h2 font-bold text-praesto-paper">
            Chi entra sa già cosa c&apos;è da fare.
          </p>
          <p className="text-sm text-sidebar-muted">
            Procedure, turni e controlli. In un posto solo, sul telefono di chi lavora.
          </p>
          <SpectrumBar value={100} dark />
        </div>
      </div>
    </div>
  );
}
